import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Checkbox, Col, Form, Input, InputNumber, Row, Space, Switch, Typography } from "antd";
import { MailOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { EMAIL_PLUGIN_ENDPOINT } from "../constants";

const { Text } = Typography;

/**
 * The platform's SMTP connection — every email the system sends (welcome mails, invoices,
 * payment reminders, alerts) goes through it once it is configured; until then the server
 * falls back to its appsettings.
 *
 * The password is stored encrypted and never sent to the browser, so the field starts empty
 * and an empty field means "keep the one on file" — both here and on the server.
 */
const EmailSmtpPanel = ({ onChanged }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effect — same pattern as StoragePanel.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [form] = Form.useForm();
  const [testForm] = Form.useForm();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);

    await mutateRef.current("emailSettings", {
      method: "get",
      endpoint: EMAIL_PLUGIN_ENDPOINT,
      onSuccess: (data) => {
        setSettings(data);
        form.setFieldsValue({
          smtpHost: data?.smtpHost ?? "",
          smtpPort: data?.smtpPort ?? 587,
          useImplicitSsl: data?.useImplicitSsl ?? false,
          smtpUser: data?.smtpUser ?? "",
          smtpPassword: "",
          fromName: data?.fromName ?? "",
          fromAddress: data?.fromAddress ?? "",
          isActive: data?.isActive ?? true,
        });
      },
    });

    setLoading(false);
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  /** Everything the API needs, straight off the form. */
  const payload = (values) => ({
    smtpHost: (values.smtpHost || "").trim(),
    smtpPort: values.smtpPort ?? 587,
    useImplicitSsl: values.useImplicitSsl ?? false,
    smtpUser: (values.smtpUser || "").trim(),
    smtpPassword: values.smtpPassword || "",
    fromName: (values.fromName || "").trim(),
    fromAddress: (values.fromAddress || "").trim(),
    isActive: values.isActive ?? true,
  });

  /** 587 and 465 imply each other's TLS mode, so flipping the box fixes the port too. */
  const onSslToggle = (e) => {
    const port = form.getFieldValue("smtpPort");
    if (e.target.checked && port === 587) form.setFieldValue("smtpPort", 465);
    if (!e.target.checked && port === 465) form.setFieldValue("smtpPort", 587);
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);

    await mutate("saveEmailSettings", {
      method: "put",
      endpoint: EMAIL_PLUGIN_ENDPOINT,
      payload: payload(values),
      onSuccess: (data) => {
        setSettings(data);
        // The password was either kept or replaced; either way the box goes back to "unchanged".
        form.setFieldValue("smtpPassword", "");
        onChanged?.();
      },
    });

    setSaving(false);
  };

  const sendTest = async () => {
    const values = await form.validateFields();
    const { to } = await testForm.validateFields();
    setTesting(true);
    setTestResult(null);

    await mutate("testEmailSettings", {
      method: "post",
      endpoint: `${EMAIL_PLUGIN_ENDPOINT}/test`,
      // The stored password stays on the server: an empty field lets it fill the blank in.
      payload: { ...payload(values), to: to.trim() },
      onSuccess: (data) => {
        setTestResult(data);
        // The test stamps LastTestAt on the connection, so re-read it for the banner.
        load();
      },
    });

    setTesting(false);
  };

  const lastTest = settings?.lastTestAt ? new Date(settings.lastTestAt).toLocaleString() : null;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="One connection, used by every email the system sends"
        description="Welcome mails, invoices, payment reminders and alerts all go through this server. The password is stored encrypted and never leaves the server; until the connection is configured, the server's own configuration is used."
      />

      <Card
        title={
          <Space size={8}>
            <MailOutlined />
            Email (SMTP)
          </Space>
        }
        loading={loading}
      >
        {lastTest && (
          <Alert
            style={{ marginBottom: 16 }}
            type={settings.lastTestOk ? "success" : "error"}
            showIcon
            message={
              settings.lastTestOk
                ? `Last test succeeded: ${lastTest}`
                : `Last test failed: ${lastTest}`
            }
            description={settings.lastTestOk ? null : settings.lastTestError}
          />
        )}

        <Form form={form} layout="vertical" initialValues={{ smtpPort: 587, useImplicitSsl: false, isActive: true }}>
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="smtpHost"
                label="SMTP host"
                rules={[{ required: true, message: "An SMTP host is required." }]}
              >
                <Input placeholder="smtp.gmail.com" autoComplete="off" maxLength={300} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="smtpPort"
                label="Port"
                rules={[{ required: true, message: "A port is required." }]}
              >
                <InputNumber min={1} max={65535} style={{ width: "100%" }} placeholder="587" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="useImplicitSsl" valuePropName="checked" style={{ marginBottom: 16 }}>
            <Checkbox onChange={onSslToggle}>
              <Space size={6}>
                Secure connection (SSL/465)
                <Text type="secondary" style={{ fontSize: 12 }}>
                  (off means STARTTLS on port 587 — what Gmail expects)
                </Text>
              </Space>
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="smtpUser"
            label="User (email)"
            rules={[{ required: true, message: "A user is required." }]}
          >
            <Input placeholder="mailbox@gmail.com" autoComplete="off" maxLength={300} />
          </Form.Item>

          <Form.Item
            name="smtpPassword"
            label="App password / API key"
            tooltip={
              settings?.hasPassword
                ? "Leave empty to keep the stored password."
                : "Stored encrypted; it is never sent back to this screen."
            }
            rules={
              settings?.hasPassword
                ? []
                : [{ required: true, message: "An app password is required." }]
            }
          >
            <Input.Password
              autoComplete="new-password"
              placeholder={settings?.hasPassword ? "unchanged — leave empty to keep it" : ""}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="fromName"
                label="Sender name"
                rules={[{ required: true, message: "A sender name is required." }]}
              >
                <Input placeholder="Kubit" autoComplete="off" maxLength={200} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="fromAddress"
                label="Sender email"
                rules={[
                  { required: true, message: "A sender email is required." },
                  { type: "email", message: "That is not a valid email address." },
                ]}
              >
                <Input placeholder="mailbox@gmail.com" autoComplete="off" maxLength={300} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
            tooltip="When off, the system falls back to the server's own email configuration."
          >
            <Switch />
          </Form.Item>

          <Button variant="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
            Save configuration
          </Button>
        </Form>
      </Card>

      <Card
        title={
          <Space size={8}>
            <SendOutlined />
            Send a test email
          </Space>
        }
      >
        <Form form={testForm} layout="vertical">
          <Row gutter={16} align="bottom">
            <Col xs={24} md={16}>
              <Form.Item
                name="to"
                label="Destination email"
                rules={[
                  { required: true, message: "A destination is required." },
                  { type: "email", message: "That is not a valid email address." },
                ]}
              >
                <Input placeholder="test@example.com" autoComplete="off" maxLength={300} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item>
                <Button variant="secondary" icon={<SendOutlined />} loading={testing} onClick={sendTest}>
                  Send test
                </Button>
              </Form.Item>
            </Col>
          </Row>

          {testResult && (
            <Alert
              type={testResult.sent ? "success" : "error"}
              showIcon
              message={
                testResult.sent
                  ? `Test email sent to ${testResult.to} in ${testResult.elapsedMs} ms`
                  : "The test email could not be sent"
              }
              description={testResult.sent ? "Check the inbox (and the spam folder) to confirm it arrived." : testResult.error}
            />
          )}
        </Form>
      </Card>
    </Space>
  );
};

export default EmailSmtpPanel;
